/**
 * The account this browser last signed in with (round 10, C3): the sign-in page offers
 * "Continue as a•••@x.com" for it, and a click tries a silent sign-in with the address as the
 * hint before any redirect (`MsalTokenProvider.signIn`).
 *
 * The address and the provider id only, never a token. Written after a successful sign-in, kept
 * by a local sign-out (an idle logoff, another tab's sign-out), cleared by the Sign out button
 * and by "Use another account". Every access is wrapped: with site data blocked the page still
 * renders, it just offers no Continue.
 */

export const LAST_ACCOUNT_KEY = "skydata.lastAccount";

/** An address is at most 320 characters (RFC 5321); anything longer is not one we wrote. */
const MAX_ADDRESS_LENGTH = 320;

export interface LastAccount {
  address: string;
  providerId: string;
}

export function readLastAccount(): LastAccount | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(LAST_ACCOUNT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const { address, providerId } = parsed as { address?: unknown; providerId?: unknown };
  if (typeof address !== "string" || typeof providerId !== "string" || !providerId) return null;
  if (address.length > MAX_ADDRESS_LENGTH || address.lastIndexOf("@") <= 0) return null;
  return { address, providerId };
}

export function writeLastAccount(account: LastAccount): void {
  try {
    localStorage.setItem(
      LAST_ACCOUNT_KEY,
      JSON.stringify({ address: account.address, providerId: account.providerId }),
    );
  } catch {
    /* site data blocked; the page offers no Continue next time */
  }
}

export function clearLastAccount(): void {
  try {
    localStorage.removeItem(LAST_ACCOUNT_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * "a•••@x.com": the first letter of the local part and the domain, as the Watch menu masks the
 * caller's address (`maskNotifyAddress` on the server). A one-letter local part shows no letter.
 */
export function maskAddress(address: string): string {
  const trimmed = address.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0) return "•••";
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  if (!domain) return "•••";
  const head = local.length > 1 ? local[0] : "";
  return `${head}•••@${domain}`;
}
